import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  Stack,
  Grid,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import CertificateReviewCard from './components/certificate-review-card';
import { validateCertificate } from './components/validation-utils';
import { useSnackbar } from '@/hooks';
import { useUpdateProductCertExtractedDataMutation } from '@/services';

interface CertificateVerificationProps {
  data: CertificateExtractedData;
  formType: string;
  onSave: (data: CertificateExtractedData) => void;
  certificateId?: string; // Optional - for edit mode
}

const CertificateVerification: React.FC<CertificateVerificationProps> = ({
  data,
  formType,
  onSave,
  certificateId,
}) => {
  const [verifiedData, setVerifiedData] = useState<CertificateExtractedData>(data);
  const [expandedSections, setExpandedSections] = useState<string[]>(['items']);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setVerifiedData(data);
    setExpandedSections(['items']);
    setIsSaving(false);
  }, [data]);
  
  const { show } = useSnackbar();
  const [updateCertificate] = useUpdateProductCertExtractedDataMutation();

  // Validation statistics
  const validationStats = useMemo(() => {
    const issues = validateCertificate(verifiedData, formType);
    
    let validCount = 0;
    let errorCount = 0;
    let warningCount = 0;

    issues.forEach(issue => {
      if (issue.type === 'error') {
        errorCount++;
      } else if (issue.type === 'warning') {
        warningCount++;
      }
    });

    const itemIssues = verifiedData.items?.map(item => validateCertificate({ items: [item] }, formType)) || [];
    const hasItemErrors = itemIssues.some(issues => issues.some(issue => issue.type === 'error'));
    
    if (!hasItemErrors && errorCount === 0) {
      validCount = 1;
    }

    return { validCount, errorCount, warningCount, issues };
  }, [verifiedData, formType]);

  const handleDataUpdate = useCallback((updatedData: CertificateExtractedData) => {
    setVerifiedData(updatedData);
  }, []);

  const handleToggleSection = useCallback((section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  }, []);

  // Helper to transform data to input format
  const transformToInputFormat = (data: CertificateExtractedData): UpdateExtractedDataInput => {
    return {
      certifier: data.certifier || null,
      certifierDetails: data.certifierDetails ? {
        yourName: data.certifierDetails.yourName || null,
        title: data.certifierDetails.title || null,
        companyName: data.certifierDetails.companyName || null,
        address: data.certifierDetails.address || null,
        countryAndPostalCode: data.certifierDetails.countryAndPostalCode || null,
        phone: data.certifierDetails.phone || null,
        email: data.certifierDetails.email || null,
      } : undefined,
      exporterDetails: data.exporterDetails ? {
        companyName: data.exporterDetails.companyName || null,
        address: data.exporterDetails.address || null,
        countryAndPostalCode: data.exporterDetails.countryAndPostalCode || null,
        phone: data.exporterDetails.phone || null,
        email: data.exporterDetails.email || null,
      } : undefined,
      producerDetails: data.producerDetails ? {
        companyName: data.producerDetails.companyName || null,
        address: data.producerDetails.address || null,
        countryAndPostalCode: data.producerDetails.countryAndPostalCode || null,
        phone: data.producerDetails.phone || null,
        email: data.producerDetails.email || null,
      } : undefined,
      importerDetails: data.importerDetails ? {
        companyName: data.importerDetails.companyName || null,
        address: data.importerDetails.address || null,
        countryAndPostalCode: data.importerDetails.countryAndPostalCode || null,
        phone: data.importerDetails.phone || null,
        email: data.importerDetails.email || null,
      } : undefined,
      items: data.items?.map(item => ({
        partNumber: item.partNumber || null,
        description: item.description || null,
        hts: item.hts || null,
        invoiceNumber: item.invoiceNumber || null,
        originCriterion: item.originCriterion || null,
        order: item.order || null,
        ship: item.ship || null,
        net: item.net || null,
        amount: item.amount || null,
        origin: item.origin || null,
        qtyPerPallet: item.qtyPerPallet || null,
        INCLUDED: item.INCLUDED || null,
      })) || [],
    };
  };

  const handleCertifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerifiedData({
      ...verifiedData,
      certifier: event.target.value as "Importer" | "Exporter" | "Producer",
    });
  };

  const handleSave = async () => {
    if (validationStats.errorCount > 0) {
      show(
        'Cannot save',
        'Please fix all errors before saving',
        'error'
      );
      return;
    }

    setIsSaving(true);
    try {
      if (certificateId) {
        // Update existing certificate
        const inputData = transformToInputFormat(verifiedData);
        await updateCertificate({
          id: certificateId,
          extractedData: inputData as any, // Type assertion needed due to GraphQL input type
        }).unwrap();
        show('Success', 'Certificate updated successfully', 'success');
      }
      onSave(verifiedData);
    } catch (error) {
      show(
        'Save failed',
        'Failed to save certificate data. Please try again.',
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Verify Certificate Data
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Review and edit the extracted data before saving. Fix any errors and warnings.
        </Typography>

        {!verifiedData.certifier && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Action Required:</strong> The API did not identify who the certifier is. You must select whether the certifier is the Exporter, Importer, or Producer before proceeding.
          </Alert>
        )}

        {/* Certifier Selection */}
        <Box sx={{ 
          mt: 3, 
          mb: 3, 
          p: 2, 
          bgcolor: !verifiedData.certifier ? '#ffebee' : '#f5f5f5', 
          borderRadius: 1,
          border: !verifiedData.certifier ? '2px solid #f44336' : 'none'
        }}>
          <FormControl component="fieldset" error={!verifiedData.certifier}>
            <FormLabel component="legend" sx={{ fontWeight: 600, color: !verifiedData.certifier ? '#f44336' : '#374151', mb: 1 }}>
              {!verifiedData.certifier ? '⚠️ ' : ''}Select Certifier Type * {!verifiedData.certifier && '(Required)'}
            </FormLabel>
            <RadioGroup
              row
              value={verifiedData.certifier || ''}
              onChange={handleCertifierChange}
            >
              <FormControlLabel 
                value="Exporter" 
                control={<Radio />} 
                label="Exporter" 
              />
              <FormControlLabel 
                value="Importer" 
                control={<Radio />} 
                label="Importer" 
              />
              <FormControlLabel 
                value="Producer" 
                control={<Radio />} 
                label="Producer" 
              />
            </RadioGroup>
            {!verifiedData.certifier && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                Please select a certifier type
              </Typography>
            )}
          </FormControl>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Important:</strong> The certifier represents one of these parties:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>If certifier is <strong>Exporter</strong> → Leave "Exporter Details" section blank</li>
              <li>If certifier is <strong>Importer</strong> → Leave "Importer Details" section blank</li>
              <li>If certifier is <strong>Producer</strong> → Leave "Producer Details" section blank</li>
            </ul>
            Fill in the Certifier Details section with the information of the selected party.
          </Alert>
        </Box>

        {/* Statistics */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`${validationStats.validCount} Valid`}
              color="success"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Chip
              icon={<WarningIcon />}
              label={`${validationStats.warningCount} Warnings`}
              color="warning"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Chip
              icon={<ErrorIcon />}
              label={`${validationStats.errorCount} Errors`}
              color="error"
              variant="outlined"
            />
          </Grid>
        </Grid>

        {validationStats.errorCount > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Please fix all errors before saving. Only items marked as "Included" require complete information.
          </Alert>
        )}

        {formType === 'USMCA' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Items with origin from Canada or Mexico are automatically marked as included for USMCA.
          </Alert>
        )}
      </Paper>

      {/* Certificate Sections */}
      <Stack spacing={3}>
        {/* Certifier Details */}
        <CertificateReviewCard
          section="certifier"
          title="Certifier Details"
          data={verifiedData}
          onUpdate={handleDataUpdate}
          expanded={expandedSections.includes('certifier')}
          onToggle={() => handleToggleSection('certifier')}
          formType={formType}
          validationIssues={validationStats.issues.filter(i => i.field?.startsWith('certifierDetails'))}
        />

        {/* Exporter Details */}
        <CertificateReviewCard
          section="exporter"
          title="Exporter Details"
          data={verifiedData}
          onUpdate={handleDataUpdate}
          expanded={expandedSections.includes('exporter')}
          onToggle={() => handleToggleSection('exporter')}
          formType={formType}
          validationIssues={validationStats.issues.filter(i => i.field?.startsWith('exporter'))}
        />

        {/* Producer Details */}
        <CertificateReviewCard
          section="producer"
          title="Producer Details"
          data={verifiedData}
          onUpdate={handleDataUpdate}
          expanded={expandedSections.includes('producer')}
          onToggle={() => handleToggleSection('producer')}
          formType={formType}
          validationIssues={validationStats.issues.filter(i => i.field?.startsWith('producer'))}
        />

        {/* Importer Details */}
        <CertificateReviewCard
          section="importer"
          title="Importer Details"
          data={verifiedData}
          onUpdate={handleDataUpdate}
          expanded={expandedSections.includes('importer')}
          onToggle={() => handleToggleSection('importer')}
          formType={formType}
          validationIssues={validationStats.issues.filter(i => i.field?.startsWith('importer'))}
        />

        {/* Items */}
        <CertificateReviewCard
          section="items"
          title={`Items (${verifiedData.items?.length || 0})`}
          data={verifiedData}
          onUpdate={handleDataUpdate}
          expanded={expandedSections.includes('items')}
          onToggle={() => handleToggleSection('items')}
          formType={formType}
          validationIssues={validationStats.issues.filter(i => i.field?.startsWith('items'))}
        />
      </Stack>

      {/* Actions */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isSaving || validationStats.errorCount > 0}
            size="large"
          >
            {isSaving ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CertificateVerification;